import dayjs from 'dayjs'

import type { NearbySpaceListItem } from 'features/space/types/nearbySpace'
import type { RecommendedSpace, RecommendedSpacePhoto } from 'types/space/recommended'
import type { SpaceBooking } from 'state/ducks/locations/models'

const BAG_SIZE = 'small'
const SUITCASE_SIZE = 'large'

const formatPrice = (price: number): string => `¥${price.toLocaleString()}`

const getCapacityQuantity = (capacity: NearbySpaceListItem['capacity'], size: string): number => {
  return capacity?.find((item) => item.size === size)?.quantity ?? 0
}

const getPriceLabel = (prices: NearbySpaceListItem['prices'], size: string): string => {
  const price = prices?.find((item) => item.size === size)?.price
  return price !== undefined ? formatPrice(price) : ''
}

const toSpacePhotos = (space: NearbySpaceListItem): RecommendedSpacePhoto[] => {
  const photos =
    space.space_photos
      ?.filter((photo): photo is RecommendedSpacePhoto => !!photo.url)
      .map((photo) => ({
        url: photo.url as string,
        photo_description: photo.photo_description,
      })) ?? []

  if (photos.length > 0) {
    return photos
  }

  if (space.main_image_url) {
    return [{ url: space.main_image_url, photo_description: '' }]
  }

  return []
}

const toBookings = (
  weeklyAvailabilities: NearbySpaceListItem['weekly_availabilities']
): SpaceBooking[] => {
  return (
    weeklyAvailabilities?.map((availability) => {
      const date = dayjs(availability.date)
      return {
        date: availability.date,
        day_of_week: availability.day_of_week,
        day_of_week_label: date.format('ddd'),
        ratio: availability.ratio,
        isHoliday: availability.is_holiday,
        capacity: availability.capacity,
      }
    }) ?? []
  )
}

const toReviews = (
  space: Pick<NearbySpaceListItem, 'top_reviews' | 'total_review_count' | 'average_review_rating'>
): RecommendedSpace['reviews'] => {
  const { top_reviews: topReviews, total_review_count, average_review_rating } = space

  if (!topReviews || topReviews.length === 0) {
    return {
      comment: '',
      user_name: '',
      total_review_count: total_review_count ?? 0,
      average_review: average_review_rating ?? 0,
    }
  }

  const firstReview = topReviews[0]
  const averageReview =
    average_review_rating ??
    topReviews.reduce((sum, review) => sum + review.rating, 0) / topReviews.length

  return {
    comment: firstReview.comment,
    user_name: `${firstReview.review_user.last_name}${firstReview.review_user.first_name}`,
    total_review_count: total_review_count ?? 0,
    average_review: averageReview,
  }
}

const toTodayBusinessHours = (
  workingHours: NearbySpaceListItem['working_hours']
): Pick<RecommendedSpace, 'open_at_today' | 'close_at_today' | 'available_24_hour'> => {
  if (!workingHours) {
    return { open_at_today: '', close_at_today: '', available_24_hour: false }
  }

  return {
    open_at_today: workingHours.open_at ?? '',
    close_at_today: workingHours.close_at ?? '',
    available_24_hour: workingHours.is_24_hours,
  }
}

export const mapNearbySpaceToRecommendedSpace = (space: NearbySpaceListItem): RecommendedSpace => {
  const spacePhotos = toSpacePhotos(space)
  const nearestStation = space.nearby_stations?.[0]
  const todayBusinessHours = toTodayBusinessHours(space.working_hours)

  return {
    id: space.space_id,
    encrypted_id: space.encrypted_id,
    name: space.name,
    space_photo: spacePhotos[0],
    space_photos: spacePhotos,
    bag_size: getCapacityQuantity(space.capacity, BAG_SIZE),
    suitcase_size: getCapacityQuantity(space.capacity, SUITCASE_SIZE),
    ...todayBusinessHours,
    open_at_mon: '',
    close_at_mon: '',
    open_at_tue: '',
    close_at_tue: '',
    open_at_wed: '',
    close_at_wed: '',
    open_at_thu: '',
    close_at_thu: '',
    open_at_fri: '',
    close_at_fri: '',
    open_at_sat: '',
    close_at_sat: '',
    open_at_sun: '',
    close_at_sun: '',
    can_book_on_the_day: false,
    can_book_multiple_days: false,
    station_1_name: nearestStation?.station_name ?? '',
    station_1_en_name: nearestStation?.station_name ?? '',
    distance_from_station_1: nearestStation?.distance ?? 0,
    zip_code: '',
    address: '',
    latitude: 0,
    longitude: 0,
    google_place_url: '',
    is_available_on_sun: 0,
    is_available_on_mon: 0,
    is_available_on_tue: 0,
    is_available_on_wed: 0,
    is_available_on_thu: 0,
    is_available_on_fri: 0,
    is_available_on_sat: 0,
    bookings: toBookings(space.weekly_availabilities),
    prices: {
      bag_size: getPriceLabel(space.prices, BAG_SIZE),
      suitecase_size: getPriceLabel(space.prices, SUITCASE_SIZE),
    },
    reviews: toReviews(space),
  }
}
